import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Card, Stack, TextField, Typography, Icon, Chip } from "@mui/material";
import { getCurrentContract, publishContract } from "services/contracts";

export default function ContractAdmin() {
  const input = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [current, setCurrent] = useState(null);
  const [title, setTitle] = useState("Contrato de uso da plataforma PSO");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    getCurrentContract()
      .then((result) => {
        if (active) setCurrent(result.contract);
      })
      .catch((failure) => {
        if (active) setError(failure.message);
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const chooseFile = (selected) => {
    if (busy || !selected) return;
    setSuccess("");
    if (selected.size === 0 || selected.size > 5 * 1024 * 1024 || !/\.pdf$/i.test(selected.name)) {
      setFile(null);
      setError("Selecione um PDF de até 5 MB.");
      return;
    }
    setError("");
    setFile(selected);
  };
  const submit = async () => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await publishContract(title, file);
      setCurrent(result.contract);
      setFile(null);
      setSuccess(
        "Nova versão publicada. Os autores deverão aceitá-la para continuar usando a plataforma."
      );
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h5">Contrato da plataforma</Typography>
        {current && (
          <Typography variant="body2">
            Em vigor: {current.isDemo ? "Contrato de uso da plataforma" : current.title} · versão{" "}
            {current.versionId}
          </Typography>
        )}
        {current && (
          <Button component="a" href={current.pdfUrl} target="_blank" rel="noopener noreferrer">
            Ver PDF atual
          </Button>
        )}
        <Alert severity="info">
          Cada publicação cria uma versão e exige novo aceite dos autores. O histórico dos
          documentos e dos aceites é preservado.
        </Alert>
        <TextField
          label="Título do contrato"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 180 }}
          disabled={busy}
        />
        <Box
          role="button"
          tabIndex={busy ? -1 : 0}
          aria-label="Enviar contrato PDF: selecionar ou arrastar arquivo"
          aria-disabled={busy}
          onClick={() => {
            if (!busy) input.current?.click();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              if (!busy) input.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (busy) return;
            if (event.dataTransfer.files.length !== 1) {
              setFile(null);
              setError("Envie um PDF por vez.");
              return;
            }
            chooseFile(event.dataTransfer.files[0]);
          }}
          sx={{
            cursor: busy ? "default" : "pointer",
            textAlign: "center",
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            border: "2px dashed",
            borderColor: dragging ? "#138735" : "#b7cebe",
            bgcolor: dragging ? "#e7f6ec" : "#f7faf8",
            transition: "background-color 150ms",
            "&:focus-visible": { outline: "2px solid #138735", outlineOffset: 3 },
          }}
        >
          <Icon sx={{ fontSize: 40, color: "#138735", mb: 1 }}>upload_file</Icon>
          <Typography variant="h6">
            {dragging ? "Solte o PDF aqui" : "Arraste o contrato em PDF para cá"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            ou clique para selecionar · Até 5 MB
          </Typography>
        </Box>
        <input
          ref={input}
          hidden
          type="file"
          accept="application/pdf,.pdf"
          disabled={busy}
          onChange={(event) => {
            chooseFile(event.target.files[0]);
            event.target.value = "";
          }}
        />
        {file && (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Chip
              icon={<Icon>picture_as_pdf</Icon>}
              label={`${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB`}
            />
            <Button disabled={busy} onClick={() => setFile(null)}>
              Remover arquivo
            </Button>
          </Stack>
        )}
        {preview && (
          <Box
            component="iframe"
            title="Prévia do novo contrato"
            src={preview}
            sx={{ width: "100%", height: 400, border: "1px solid #ddd" }}
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <Button
          variant="contained"
          color="success"
          disabled={!file || !title.trim() || busy}
          onClick={submit}
        >
          {busy ? "Publicando..." : "Publicar PDF e exigir novo aceite"}
        </Button>
      </Stack>
    </Card>
  );
}
